import React, { createContext, ReactNode, useCallback, useState, useMemo, useContext } from 'react';
import { DeveloperToolsContext, OverrideKeys, Overrides } from './types';
import { useDeveloperToolsEnabled } from './useDeveloperToolsEnabled';
import { saveContextToLocalStorage, getContextFromLocalStorage } from './localStorage';

const defaultValues: Overrides = {
  permissions: {},
  featureFlags: {},
  mfeEntries: {},
};

const contextDefaults: DeveloperToolsContext = {
  overrides: defaultValues,
  setOverrides: () => {},
  showOverridesOnly: false,
  setShowOverridesOnly: () => {},
  resetOverride: () => {},
  selectedTab: 'featureFlags',
  setSelectedTab: () => {},
};

const Context = createContext<DeveloperToolsContext>(contextDefaults);
const getDefaults = (): Overrides => {
  const localStorageOverrides: Overrides = getContextFromLocalStorage();
  return { ...defaultValues, ...localStorageOverrides };
};

export const DeveloperToolsProvider = ({ children }: { children: ReactNode }) => {
  const [overrides, setOverrides] = useState<Overrides>(getDefaults);
  const [showOverridesOnly, setShowOverridesOnly] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<OverrideKeys>('permissions');

  const updateOverrides = useCallback(
    (newOverrides: Overrides) => {
      saveContextToLocalStorage(newOverrides);
      setOverrides(newOverrides);
    },
    [setOverrides],
  );

  const resetOverride = useCallback(() => {
    updateOverrides({ ...overrides, [selectedTab]: {} });
  }, [updateOverrides, overrides]);

  const value: DeveloperToolsContext = useMemo(() => {
    return {
      overrides,
      setOverrides: updateOverrides,
      showOverridesOnly,
      setShowOverridesOnly,
      resetOverride,
      selectedTab,
      setSelectedTab,
    };
  }, [
    overrides,
    setOverrides,
    showOverridesOnly,
    setShowOverridesOnly,
    resetOverride,
    selectedTab,
    setSelectedTab,
  ]);

  const isDeveloperToolsEnabled = useDeveloperToolsEnabled();

  if (!isDeveloperToolsEnabled) {
    return <>{children}</>;
  }

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useDeveloperToolsProvider = (): DeveloperToolsContext => {
  const contextValue: DeveloperToolsContext = useContext(Context);

  return contextValue;
};
